/* eslint‑disable react/prop‑types */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    TablePagination,
} from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDate_MM_YYYY, formatDateRangeFromString } from '../../../Utils/FormatDate';
import { beautifyNumber } from '../../../Utils/FormatNumber';

/* ───────────── helpers ───────────── */
const cellFmt = (v) => {
    const formatted = beautifyNumber(v || 0);
    // Check if the formatted number contains parentheses (negative numbers)
    if (formatted.includes('(') && formatted.includes(')')) {
        return <span style={{ color: '#d32f2f' }}>{formatted}</span>;
    }
    return formatted;
};

/* ───────────── component ───────────── */
const TrialBalanceTable = ({
    palDates,
    isMonthly,
    palCodesData,
    footerTotals,
    handleSortChange,
    handleSelect,
    handleRightClickedRow,
    selectedSearchType,
    searchQuery,
    showAccountCardsWithZeros
}) => {
    
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();

    /* pagination */
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const tableContainerRef = useRef(null);
    
    useEffect(() => setCurrentPage(0), [palCodesData]);

    const paginatedData = useMemo(() => {
        if (!palCodesData?.length) return [];
        const start = currentPage * rowsPerPage;
        const end = start + rowsPerPage;
        return palCodesData.slice(start, end);
    }, [palCodesData, currentPage, rowsPerPage]);

    /* expansion */
    const [openRows, setOpenRows] = useState({});
    const toggle = (index) => setOpenRows((o) => ({ ...o, [index]: !o[index] }));

    const headStyle = {
        backgroundColor: '#E3E7FE',
        color: '#1222b3',
        fontWeight: 600,
        fontSize: '13px',
        textAlign: 'right',
        padding: 10,
    };

    const cellStyle = {
        textAlign: 'right',
        padding: '8px 4px',
    };

    const expandCellStyle = {
        textAlign: 'center',
        padding: '4px',
        border: '0.1px solid #e0e0e0',
        width: '50px',
        textWeight: 'bold',
        background: '#f5f5f5',
    };

    const descriptionCellStyle = {
        textAlign: 'right',
        padding: '8px 8px',
        marginRight: '5px',
        border: '0.5px solid #e0e0e0',
        fontWeight: 500,
        width: '150px', // Decreased width for Description column
    };

    const footerStyle = {
        background: 'rgb(251 251 251)',
        textAlign: 'right',
        marginLeft: '5px',
        fontWeight: 'bold',
        padding: '8px 4px',
        border: '0.2px solid #e0e0e0',
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    /* ───────────── JSX ───────────── */
    return (
        <div className={dir} style={{ height: '65vh', width: '100%' }}>
            <TableContainer component={Paper} sx={{ maxHeight: '60vh' }} ref={tableContainerRef}>
                <Table stickyHeader>
                    {/* ① header */}
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ ...expandCellStyle, backgroundColor: '#E3E7FE' }} />
                            <TableCell 
                                style={headStyle}
                                onClick={() => handleSortChange('description', 'descending')}
                            >
                                {t('Description')}
                            </TableCell>
                            {[...palDates].map((date) => (
                                <TableCell
                                    key={date}
                                    style={headStyle}
                                    onClick={() => handleSortChange('dateValues', 'number')}
                                >
                                    {date === 'InitialBalance'
                                        ? t('InitialBalance')
                                        : isMonthly
                                            ? formatDate_MM_YYYY(date)
                                            : formatDateRangeFromString(date)}
                                </TableCell>
                            ))}
                            <TableCell 
                                style={headStyle}
                                onClick={() => handleSortChange('total', 'descending')}
                            >
                                {t('Total')}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {/* ② body */}
                    <TableBody>
                        {paginatedData?.map((palCode, index) => (
                            <Row
                                key={palCode.palCodeId}
                                palCode={palCode}
                                dateHeaders={palDates}
                                open={!!openRows[index]}
                                handleToggleRow={() => toggle(index)}
                                onRightClick={handleRightClickedRow}
                                handleSelect={handleSelect}
                                selectedSearchType={selectedSearchType}
                                searchQuery={searchQuery}
                                showAccountCardsWithZeros={showAccountCardsWithZeros}
                                t={t}
                                cellStyle={cellStyle}
                                expandCellStyle={expandCellStyle}
                                descriptionCellStyle={descriptionCellStyle}
                            />
                        ))}

                        {/* ③ footer (sticky) */}
                        <TableRow
                            sx={{
                                background: '#e8e8e8',
                                position: 'sticky',
                                bottom: 0,
                            }}
                        >
                            <TableCell style={expandCellStyle} />
                            <TableCell style={footerStyle}>
                                <strong>{t('Total')}</strong>
                            </TableCell>
                            {[...palDates].map((date) => (
                                <TableCell key={date} style={footerStyle}>
                                    <strong>{cellFmt(footerTotals.dateTotals[date] || 0)}</strong>
                                </TableCell>
                            ))}
                            <TableCell style={footerStyle}>
                                <strong>{cellFmt(footerTotals.grandTotal)}</strong>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                {/* ④ pagination */}
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[25, 50, 100]}
                    count={palCodesData?.length}
                    rowsPerPage={rowsPerPage}
                    page={currentPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('Rows per page')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('of')} ${count !== -1 ? count : `> ${to}`}`
                    }
                />
            </TableContainer>
        </div>
    );
};

export default TrialBalanceTable;

// Row Component
const Row = ({
    palCode,
    dateHeaders,
    open,
    handleToggleRow,
    onRightClick,
    handleSelect,
    selectedSearchType,
    searchQuery,
    showAccountCardsWithZeros,
    t,
    cellStyle,
    expandCellStyle,
    descriptionCellStyle,
}) => {
    // High-level total sum for the palCode (sum of all accountCards)
    const palCodeTotal = palCode.accountCards.reduce((sum, accountCard) => {
        return (
            sum +
            accountCard.dateAmounts.reduce(
                (acc, dateAmount) => acc + (dateAmount ? dateAmount.amount : 0),
                0
            )
        );
    }, 0);

    return (
        <>
            <TableRow 
                hover
                sx={{
                    backgroundColor: open ? 'linear-gradient(135deg, rgb(227, 242, 253) 0%, rgb(224, 239, 251) 100%)' : 'inherit',
                }}
            >
                <TableCell style={expandCellStyle}>
                    <IconButton
                        size="small"
                        onClick={handleToggleRow}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>

                <TableCell style={descriptionCellStyle}>
                    {palCode.palCodeId === 'NonPalCode'
                        ? t(palCode.palCodeId)
                        : palCode.palCodeId}
                </TableCell>

                {[...dateHeaders].map((date) => (
                    <TableCell key={date} style={cellStyle}>
                        {cellFmt(
                            palCode.accountCards.reduce((sum, accountCard) => {
                                const dateAmount = accountCard.dateAmounts.find(
                                    (da) => da.date === date
                                );
                                return sum + (dateAmount ? dateAmount.amount : 0);
                            }, 0)
                        )}
                    </TableCell>
                ))}
                
                <TableCell style={cellStyle}>
                    <strong>{cellFmt(palCodeTotal)}</strong>
                </TableCell>
            </TableRow>

            {(open || (selectedSearchType === 'AccountCard' && searchQuery)) &&
                palCode.accountCards
                    .filter((accountCard) =>
                        showAccountCardsWithZeros
                            ? true
                            : accountCard.dateAmounts.every((da) => da.amount === 0) === false
                    )
                    .map((accountCard, index) => {
                        const accountCardTotal = accountCard.dateAmounts.reduce(
                            (sum, dateAmount) =>
                                sum + (dateAmount ? dateAmount.amount : 0),
                            0
                        );

                        return (
                            <TableRow
                                key={index}
                                hover
                                onClick={() => handleSelect(accountCard, palCode)}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    onRightClick(accountCard, palCode);
                                }}
                                sx={{ 
                                    cursor: 'pointer',
                                    background: 'linear-gradient(135deg, rgb(227, 242, 253) 0%, rgb(224, 239, 251) 100%)',
                                }}
                            >
                                <TableCell style={expandCellStyle} />
                                <TableCell style={{ ...descriptionCellStyle, pl: 6 }}>
                                    {accountCard.name}
                                </TableCell>

                                {[...dateHeaders].map((date) => (
                                    <TableCell key={date} style={cellStyle}>
                                        {cellFmt(
                                            accountCard.dateAmounts.find(
                                                (da) => da.date === date
                                            )?.amount || 0
                                        )}
                                    </TableCell>
                                ))}
                                
                                <TableCell style={cellStyle}>
                                    <strong>{cellFmt(accountCardTotal)}</strong>
                                </TableCell>
                            </TableRow>
                        );
                    })}
        </>
    );
};
